<section class="bn-open-actions">
  <header class="bn-open-actions__header">
    <div class="bn-open-actions__heading">
      <h2 class="bn-open-actions__title">My Open Actions</h2>
      @if (!error() && !loading()) {
        <p class="bn-open-actions__summary">{{ summary() }}</p>
      }
    </div>
    <div
      class="bn-open-actions__toggles"
      role="group"
      aria-label="Open actions view"
    >
      <button
        type="button"
        class="bn-open-actions__toggle"
        [class.bn-open-actions__toggle--active]="viewMode() === 'timeline'"
        [attr.aria-pressed]="viewMode() === 'timeline'"
        (click)="setViewMode('timeline')"
      >
        Timeline
      </button>
      <button
        type="button"
        class="bn-open-actions__toggle"
        [class.bn-open-actions__toggle--active]="viewMode() === 'list'"
        [attr.aria-pressed]="viewMode() === 'list'"
        (click)="setViewMode('list')"
      >
        List
      </button>
    </div>
  </header>

  @if (viewMode() === 'timeline') {
    <div class="bn-open-actions__chips" role="group" aria-label="Due date">
      @for (chip of dueDateChips; track chip.value) {
        <button
          type="button"
          class="bn-open-actions__filter-chip"
          [ngClass]="filterChipClass(chip.value)"
          [attr.aria-pressed]="dueDateFilter() === chip.value"
          (click)="onChipClick(chip.value)"
        >
          <span class="bn-open-actions__filter-chip-count">{{
            chipCounts()[chip.value]
          }}</span>
          {{ chip.label }}
        </button>
      }
      @if (dueDateFilter() !== 'all') {
        <button
          type="button"
          class="bn-open-actions__show-all"
          (click)="showAllDueDates()"
        >
          Show all
        </button>
      }
    </div>

    <div class="bn-open-actions__chart-head">
      <div class="bn-open-actions__chart-title-row">
        <h3 class="bn-open-actions__chart-title">Actions due by date</h3>
        <div class="bn-open-actions__legend">
          <span class="bn-open-actions__legend-item">
            <span class="bn-open-actions__legend-swatch bn-open-actions__legend-swatch--high"></span>
            High priority
          </span>
          <span class="bn-open-actions__legend-item">
            <span class="bn-open-actions__legend-swatch bn-open-actions__legend-swatch--normal"></span>
            Normal
          </span>
        </div>
      </div>
    </div>

    <bds-scroll-region
      orientation="both"
      [maxHeight]="tableMaxHeight"
      styleClass="bn-open-actions__chart-scroll"
      ariaLabel="Open actions timeline; scroll to view all due dates"
    >
      @if (loading()) {
        <p class="bn-open-actions__status">Loading actions…</p>
      } @else if (error(); as message) {
        <p class="bn-open-actions__status" role="alert">{{ message }}</p>
      } @else {
        <div class="bn-open-actions__chart">
          @for (bar of timelineBars(); track bar.id) {
            <button
              type="button"
              class="bn-open-actions__bar"
              [class.bn-open-actions__bar--empty]="bar.total === 0"
              [class.bn-open-actions__bar--overdue]="bar.kind === 'overdue'"
              [class.bn-open-actions__bar--today]="bar.isToday"
              [attr.aria-label]="barAriaLabel(bar)"
              (click)="onBarClick(bar)"
            >
              <span class="bn-open-actions__bar-count">
                @if (bar.total > 0) {
                  {{ bar.total }}
                }
              </span>
              <span class="bn-open-actions__bar-plot">
                @if (bar.total > 0) {
                  <span
                    class="bn-open-actions__bar-stack"
                    [style.--stack-h]="stackPercent(bar) + '%'"
                  >
                    @if (bar.normal > 0) {
                      <span
                        class="bn-open-actions__bar-seg bn-open-actions__bar-seg--normal"
                        [style.flex-grow]="bar.normal"
                      ></span>
                    }
                    @if (bar.high > 0) {
                      <span
                        class="bn-open-actions__bar-seg bn-open-actions__bar-seg--high"
                        [style.flex-grow]="bar.high"
                      ></span>
                    }
                  </span>
                }
              </span>
              <span class="bn-open-actions__bar-label">{{ bar.label }}</span>
              @if (bar.sublabel) {
                <span class="bn-open-actions__bar-sublabel">{{ bar.sublabel }}</span>
              }
            </button>
          }
        </div>
      }
    </bds-scroll-region>
  } @else {
    <div class="bn-open-actions__toolbar">
      <input
        pInputText
        type="search"
        class="bn-open-actions__search"
        [ngModel]="search()"
        (ngModelChange)="search.set($event)"
        (keyup.enter)="onSearch()"
        placeholder="Search clients..."
        aria-label="Search clients"
      />
      <p-button label="Search" size="small" (onClick)="onSearch()" />
      <div class="bn-open-actions__filters">
        <p-select
          class="bn-open-actions__filter"
          [options]="dueDateOptions()"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          [ngModel]="dueDateFilter()"
          (ngModelChange)="onDueDateChange($event)"
          aria-label="Filter by due date"
        />
        <p-select
          class="bn-open-actions__filter"
          [options]="actionTypeOptions()"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          [ngModel]="actionTypeCode()"
          (ngModelChange)="onActionTypeChange($event)"
          aria-label="Filter by action type"
        />
        <p-select
          class="bn-open-actions__filter"
          [options]="priorityOptions()"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          [ngModel]="priorityCode()"
          (ngModelChange)="onPriorityChange($event)"
          aria-label="Filter by priority"
        />
        @if (!filtersDefault()) {
          <button
            type="button"
            class="bn-open-actions__reset"
            (click)="resetFilters()"
          >
            Reset filters
          </button>
        }
      </div>
    </div>

    <bds-scroll-region
      orientation="both"
      [maxHeight]="tableMaxHeight"
      styleClass="bn-open-actions__table-scroll"
      ariaLabel="Open actions table; scroll to view all rows and columns"
    >
        <table class="bn-open-actions__table">
          <thead>
            <tr>
              <th [attr.aria-sort]="ariaSort('dueDate')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('dueDate')"
                >
                  Due date
                  <i class="pi" [ngClass]="sortIconClass('dueDate')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('client')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('client')"
                >
                  Client
                  <i class="pi" [ngClass]="sortIconClass('client')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('customer')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('customer')"
                >
                  Customer
                  <i class="pi" [ngClass]="sortIconClass('customer')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('actionType')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('actionType')"
                >
                  Action type
                  <i class="pi" [ngClass]="sortIconClass('actionType')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('details')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('details')"
                >
                  Action title
                  <i class="pi" [ngClass]="sortIconClass('details')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('status')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('status')"
                >
                  Status
                  <i class="pi" [ngClass]="sortIconClass('status')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('priority')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('priority')"
                >
                  Priority
                  <i class="pi" [ngClass]="sortIconClass('priority')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('promise')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('promise')"
                >
                  Promise
                  <i class="pi" [ngClass]="sortIconClass('promise')" aria-hidden="true"></i>
                </button>
              </th>
              <th [attr.aria-sort]="ariaSort('createdAt')">
                <button
                  type="button"
                  class="bn-open-actions__sort"
                  (click)="onSort('createdAt')"
                >
                  Created on
                  <i class="pi" [ngClass]="sortIconClass('createdAt')" aria-hidden="true"></i>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr>
                <td class="bn-open-actions__status" colspan="9">Loading actions…</td>
              </tr>
            } @else if (error(); as message) {
              <tr>
                <td class="bn-open-actions__status" colspan="9" role="alert">{{ message }}</td>
              </tr>
            } @else if (displayedActions().length === 0) {
              <tr>
                <td class="bn-open-actions__status" colspan="9">No open actions.</td>
              </tr>
            } @else {
              @for (action of displayedActions(); track action.actionId) {
                <tr
                  class="bn-open-actions__row"
                  tabindex="0"
                  (click)="openAction(action)"
                  (keydown.enter)="openActionFromKeyboard($event, action)"
                  (keydown.space)="openActionFromKeyboard($event, action)"
                >
                  <td>
                    @if (action.dueDate) {
                      <span
                        class="bn-open-actions__pill"
                        [class.bn-open-actions__pill--overdue]="dueTone(action) === 'overdue'"
                        [class.bn-open-actions__pill--soon]="dueTone(action) === 'soon'"
                        [class.bn-open-actions__pill--later]="dueTone(action) === 'later'"
                      >
                        {{ action.dueDate | date: 'MM/dd/yy' }}
                      </span>
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    @if (hasClientGuid(action)) {
                      <button
                        type="button"
                        class="bn-open-actions__link"
                        (click)="openClient($event, action)"
                      >
                        {{ action.client.name ?? '—' }}
                      </button>
                    } @else {
                      {{ action.client.name ?? '—' }}
                    }
                  </td>
                  <td>{{ formatCustomer(action.client.customer) }}</td>
                  <td class="bn-open-actions__type">{{ typeLabel(action.actionTypeCode) }}</td>
                  <td class="bn-open-actions__details">{{ action.name }}</td>
                  <td>
                    <span
                      class="bn-open-actions__chip"
                      [class.bn-open-actions__chip--blocked]="action.statusCode === 'BLOCKED'"
                    >
                      {{ statusLabel(action.statusCode) }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="bn-open-actions__chip"
                      [class.bn-open-actions__chip--high]="action.priorityCode === 'HIGH'"
                    >
                      {{ priorityLabel(action.priorityCode) }}
                    </span>
                  </td>
                  <td>
                    @if (action.clientPromise) {
                      <span class="bn-open-actions__chip bn-open-actions__chip--promise">Promised</span>
                    } @else {
                      —
                    }
                  </td>
                  <td>{{ action.createdAt | date: 'MM/dd/yy' }}</td>
                </tr>
              }
            }
          </tbody>
        </table>
    </bds-scroll-region>

    @if (!loading() && !error() && dueFilteredActions().length > 0) {
      <footer class="bn-open-actions__pager">
        <span>{{ offset() + 1 }}–{{ offset() + displayedActions().length }} of {{ dueFilteredActions().length }}</span>
        <div class="bn-open-actions__pager-nav">
          <p-button
            label="Prev"
            size="small"
            severity="secondary"
            [disabled]="!canPrevious()"
            (onClick)="previousPage()"
          />
          <span>{{ pageLabel() }}</span>
          <p-button
            label="Next"
            size="small"
            severity="secondary"
            [disabled]="!canNext()"
            (onClick)="nextPage()"
          />
        </div>
      </footer>
    }
  }
</section>
